import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAdminUsersStore } from '../../store/adminUsersStore';
import type { User, UserRole, UserRoleAssignment } from '../../types';
import { WorksForSelect } from './WorksForSelect';

const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  INSIDE_SALES: 'Inside Sales',
  SALES_REP: 'Sales Rep',
  MATCHING_SUPPORT: 'Matching Support',
  DATA_STEWARD: 'Data Steward',
  CAMPAIGN_MANAGER: 'Campaign Manager',
  ASSISTANT: 'Assistant',
};

interface Props {
  user: User;
  onClose: () => void;
}

export function UserEditPanel({ user, onClose }: Props) {
  const { updateUser, addRole, removeRole, makeAssistant, fetchRoleHistory } =
    useAdminUsersStore();
  const [displayName, setDisplayName] = useState(user.displayName ?? '');
  const [email, setEmail] = useState(user.email);
  const [pendingAssistant, setPendingAssistant] = useState(false);
  const [pendingRep, setPendingRep] = useState('');
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [history, setHistory] = useState<UserRoleAssignment[]>([]);

  // Refetch role history when the user changes or after a role mutation
  // (length changes when the store refreshes the user via fetchUsers).
  useEffect(() => {
    void fetchRoleHistory(user.id).then(setHistory);
  }, [user.id, user.roles.length, fetchRoleHistory]);

  // Keep local identity fields in sync if the underlying user changes
  // (e.g. another mutation refreshes the list).
  useEffect(() => {
    setDisplayName(user.displayName ?? '');
    setEmail(user.email);
  }, [user.id, user.displayName, user.email]);

  const activeRoles = new Set(user.roles);
  const isAssistant = activeRoles.has('ASSISTANT');

  function errMessage(e: unknown, fallback: string): string {
    const err = e as { response?: { data?: { message?: string } } };
    return err.response?.data?.message ?? fallback;
  }

  function toggleAssistant() {
    if (isAssistant) {
      setConfirmingRemove(true);
      return;
    }
    setPendingAssistant((v) => !v);
    setPendingRep('');
  }

  async function confirmMakeAssistant() {
    if (!pendingRep) return;
    try {
      await makeAssistant(user.id, pendingRep);
      setPendingAssistant(false);
      setPendingRep('');
      toast.success('Assistant access granted');
    } catch (e: unknown) {
      toast.error(errMessage(e, 'Failed to make assistant'));
    }
  }

  async function confirmRemoveAssistant() {
    try {
      await removeRole(user.id, 'ASSISTANT');
      setConfirmingRemove(false);
      toast.success('Assistant access removed');
    } catch (e: unknown) {
      toast.error(errMessage(e, 'Failed to remove role'));
    }
  }

  async function changeWorksFor(worksForUserId: string) {
    if (!worksForUserId || worksForUserId === user.worksFor?.id) return;
    try {
      await updateUser(user.id, { worksForUserId });
      toast.success('Works for updated');
    } catch (e: unknown) {
      toast.error(errMessage(e, 'Failed to update works for'));
    }
  }

  async function toggleRole(role: UserRole) {
    if (activeRoles.has(role)) {
      if (user.roles.length === 1) {
        toast.error('User must have at least one role');
        return;
      }
      try {
        await removeRole(user.id, role);
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } };
        toast.error(err.response?.data?.message ?? 'Failed to remove role');
      }
    } else {
      try {
        await addRole(user.id, role);
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } };
        toast.error(err.response?.data?.message ?? 'Failed to add role');
      }
    }
  }

  async function saveIdentity() {
    const updates: { displayName?: string; email?: string } = {};
    if (displayName !== (user.displayName ?? '')) updates.displayName = displayName;
    if (email !== user.email) updates.email = email;
    if (Object.keys(updates).length === 0) return;
    try {
      await updateUser(user.id, updates);
      toast.success('User updated');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message ?? 'Failed to update');
    }
  }

  async function toggleActive() {
    if (user.isActive) {
      const n = user.assignedPeopleCount ?? 0;
      const ok = window.confirm(
        n > 0
          ? `This user owns ${n} assigned people. They will become unassigned until a manager reassigns them. Continue?`
          : 'Deactivate this user?',
      );
      if (!ok) return;
    }
    try {
      await updateUser(user.id, { isActive: !user.isActive });
      toast.success(user.isActive ? 'User deactivated' : 'User reactivated');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message ?? 'Failed to update status');
    }
  }

  async function toggleSweepExempt() {
    try {
      await updateUser(user.id, { sweepExempt: !user.sweepExempt });
      toast.success(user.sweepExempt ? 'Roster sweep exemption removed' : 'User exempted from roster sweep');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message ?? 'Failed to update roster sweep exemption');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md overflow-y-auto bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit user</h2>
          <button onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <section className="mt-6 space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Display name</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Email</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <button
            onClick={saveIdentity}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            Save identity
          </button>
        </section>

        <section className="mt-8">
          <h3 className="text-sm font-semibold">Roles</h3>
          <div className="mt-2 space-y-1">
            {(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => (
              <label key={r} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={r === 'ASSISTANT' ? isAssistant || pendingAssistant : activeRoles.has(r)}
                  disabled={r !== 'ASSISTANT' && isAssistant}
                  onChange={() => (r === 'ASSISTANT' ? toggleAssistant() : toggleRole(r))}
                />
                {ROLE_LABELS[r]}
              </label>
            ))}
          </div>
          {isAssistant && (
            <p className="mt-2 text-xs text-gray-500">Remove the Assistant role first</p>
          )}

          {pendingAssistant && !isAssistant && (
            <div className="mt-3 space-y-2 rounded border border-gray-200 p-3 dark:border-gray-700">
              <WorksForSelect
                id="edit-user-new-works-for"
                excludeUserId={user.id}
                value={pendingRep}
                onChange={setPendingRep}
              />
              <p className="text-xs text-gray-500">
                Saving replaces this user's other roles with Assistant.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={confirmMakeAssistant}
                  disabled={!pendingRep}
                  className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Make assistant
                </button>
                <button
                  onClick={toggleAssistant}
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {isAssistant && (
            <div className="mt-3 space-y-2">
              <WorksForSelect
                id="edit-user-works-for"
                excludeUserId={user.id}
                value={user.worksFor?.id ?? ''}
                current={user.worksFor}
                onChange={changeWorksFor}
              />
              {confirmingRemove && (
                <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-200">
                  <p>Remove access to {user.worksFor?.displayName ?? 'this rep'}'s book?</p>
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={confirmRemoveAssistant}
                      className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
                    >
                      Remove access
                    </button>
                    <button
                      onClick={() => setConfirmingRemove(false)}
                      className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
                    >
                      Keep
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="mt-8">
          <h3 className="text-sm font-semibold">Status</h3>
          <button
            onClick={toggleActive}
            className={`mt-2 rounded px-3 py-1.5 text-sm font-medium ${
              user.isActive
                ? 'bg-red-50 text-red-700 hover:bg-red-100'
                : 'bg-green-50 text-green-700 hover:bg-green-100'
            }`}
          >
            {user.isActive ? 'Deactivate' : 'Reactivate'}
          </button>
        </section>

        <section className="mt-8">
          <h3 className="text-sm font-semibold">Roster sweep</h3>
          <label className="mt-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!user.sweepExempt}
              onChange={toggleSweepExempt}
            />
            Exempt from nightly roster sweep
          </label>
          <p className="mt-1 text-xs text-gray-500">
            Protected from being auto-deactivated by the nightly sync. New reps are exempted
            automatically until they show up in a Blast roster; check this to keep an account
            protected beyond that.
          </p>
        </section>

        <section className="mt-8">
          <h3 className="text-sm font-semibold">Role history</h3>
          <ul className="mt-2 space-y-1 text-xs text-gray-600">
            {history.length === 0 && <li className="text-gray-400">No history yet.</li>}
            {history.map((h) => (
              <li key={h.id}>
                <span className="font-medium">{ROLE_LABELS[h.role]}</span>
                {h.isActive ? ' active' : ' revoked'} — granted by{' '}
                {h.grantedBy?.displayName ?? h.grantedBy?.email ?? 'system'} on{' '}
                {new Date(h.grantedAt).toLocaleDateString()}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
